import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClinicianList,
  ClinicianMetaData,
  // ClinicianData,
  AzureEntraUsers,
  FacilityList,
  SaveClinicianResponse,
  ClinicianRequest,
  ClinicianData,
} from '../services/ClinicianService';
import {
  getAzureEntraUsers,
  getClinicianList,
  getClinicianMetaData,
  saveOrUpdateClinitian,
  getClinicianDetailsById,
  getClinicianSpecificToFacilityIdList,
  getFacilitiesSpecificToClinicianIdList,
} from '../services/ClinicianService';

export interface ClinicianListQueryPerams {
  userId: string;
  facilityId?: number;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
}
export interface FacilityListQueryPerams {
  userId: string;
  clinicianId?: number;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
}

export const useGetClinicianList = ({
  userId,
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
  statusId,
}: ClinicianListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<ClinicianList>({
    queryKey: [
      'ClinicianListResponse',
      userId,
      page,
      size,
      sortField,
      sortType,
      searchTerm,
      statusId,
    ],
    queryFn: async () => {
      const data = await getClinicianList(
        userId,
        page,
        size,
        sortField,
        sortType,
        searchTerm,
        statusId
      );
      return data;
    },
    staleTime: 0,
    gcTime: 0,
  });

  return {
    ClinicianListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetClinicianSpecificToFacilityIdList = ({
  facilityId,
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
}: ClinicianListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<ClinicianList>({
    queryKey: [
      'ClinicianSpecificToFacilityListResponse',
      facilityId,
      page,
      size,
      sortField,
      sortType,
      searchTerm,
    ],
    queryFn: async () => {
      const data = await getClinicianSpecificToFacilityIdList(
        facilityId,
        page,
        size,
        sortField,
        sortType,
        searchTerm
      );
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!facilityId,
  });

  return {
    ClinicianSpecificToFacilityListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
export const useGetFacilitiesSpecificToClinicianIdList = ({
  clinicianId,
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
}: FacilityListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<FacilityList>({
    queryKey: [
      'FacilitiesSpecificToClinicianListResponse',
      clinicianId,
      page,
      size,
      sortField,
      sortType,
      searchTerm,
    ],
    queryFn: async () => {
      const data = await getFacilitiesSpecificToClinicianIdList(
        clinicianId,
        page,
        size,
        sortField,
        sortType,
        searchTerm
      );
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!clinicianId,
  });

  return {
    FacilitiesSpecificToClinicianListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetAzureEntraUsers = (userType: string) => {
  const { data, isLoading, isError, error, refetch } = useQuery<
    AzureEntraUsers[]
  >({
    queryKey: ['azureEntraUsers'],
    queryFn: async () => {
      const data = await getAzureEntraUsers(userType);
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: false,
  });

  return {
    azureEntraUsers: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetClinicianMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ClinicianMetaData>({
      queryKey: ['clinicianMetaData', userId],
      queryFn: async () => {
        const data = await getClinicianMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    clinicianMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateClinician = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    SaveClinicianResponse,
    Error,
    ClinicianData & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdateClinitian(userId, action, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FacilityListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating facility:', error);
    },
  });
};

export const useGetClinicianDetailsById = (
  userId: string,
  clinicianId: number
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ClinicianRequest>({
      queryKey: ['ClinicianDetailsById', userId, clinicianId],
      queryFn: async () => {
        const data = await getClinicianDetailsById(userId, clinicianId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!clinicianId,
    });

  return {
    ClinicianDetailsById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
